import * as React from 'react'
// components
import PopoverModal from 'components/UI/PopoverModal'
import AddFormFieldMenu from './components/AddFormFieldMenu'
// icons
import AddCircle from '@mui/icons-material/AddCircle'
import MenuItemOrigin from '@mui/material/MenuItem'
// constants
import { Modals } from 'constants/modals'
import { DEFAULT_MATRIX } from 'components/Modals/MatrixModal/components/Matrix/constants'
import { NO_VALUE_FIELD_TYPES, RISK_TEMPLATE_FIELDS } from 'constants/objects'
// themes
import colors from 'themes/colors'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// types
import { FieldType, IField } from 'types/graphql'
// views
import { AddFieldButtonWrapper, FieldLabel, Wrapper } from './views'
// utils
import get from 'lodash/get'
import { getIconForProperty, normalizeFieldProperty } from 'utils/fields'

const MODAL_PROPS = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const
  }
}

const INJURY_MASTER_LINKS_FIELD_TYPES = [FieldType.InjuryMasterLinks, FieldType.InjuryMasterParentLinks]

interface Props {
  label?: string
  parentField?: IField
}

const formFields = [FieldType.Form, FieldType.LinkedForm]

const AddFieldButton: React.FC<Props> = ({ label, parentField }) => {
  const { createField, riskTemplate, isInjuryMasterConfigured } = React.useContext(RiskTemplateAPIContext)

  const { showModal } = useShowModal({ modalType: Modals.PROPERTY_DETAILS_MODAL })
  const { showModal: showFormSettingsModal } = useShowModal({ modalType: Modals.RISK_TEMPLATE_ADD_FORM_MODAL })

  const addField = (field: IField, closePopup: () => void) => async () => {
    if (formFields.includes(field.fieldType)) {
      showFormSettingsModal({
        parentId: parentField.id,
        linked: field.fieldType === FieldType.LinkedForm
      })

      closePopup()
      return
    }

    const input = {
      active: true,
      entityId: riskTemplate.id,
      fieldType: field.fieldType,
      format: normalizeFieldProperty(field.format),
      label: field.label,
      multiline: !!field.multiline,
      multiple: !!field.multiple,
      position: riskTemplate.fields.filter(({ parentFieldId }) =>
        parentField ? parentFieldId === parentField.id : !parentFieldId
      ).length,
      objectEntityId: null,
      options: normalizeFieldProperty(field.options),
      required: false,
      source: get(field, 'source', []),
      standard: false,
      parentFieldId: parentField ? parentField.id : null,
      ...(field.fieldType === FieldType.RiskMatrix && { settings: DEFAULT_MATRIX })
    }

    const response = await createField(input)

    closePopup()
    showModal({
      property: {
        ...input,
        ...response
      }
    })
  }

  const options = RISK_TEMPLATE_FIELDS.filter((option) => {
    if (INJURY_MASTER_LINKS_FIELD_TYPES.includes(option.fieldType) && !isInjuryMasterConfigured) {
      return false
    }

    if (parentField?.fieldType === FieldType.Table && NO_VALUE_FIELD_TYPES.includes(option.fieldType as FieldType)) {
      return false
    }

    if (parentField?.fieldType === FieldType.Section && formFields.includes(option.fieldType as FieldType)) {
      return false
    }

    if (!parentField && formFields.includes(option.fieldType)) {
      return false
    }

    return true
  })

  return (
    <PopoverModal
      {...MODAL_PROPS}
      trigger={
        <AddFieldButtonWrapper>
          <AddCircle htmlColor={colors.blue500} />
          <span>{label || 'Add item'}</span>
        </AddFieldButtonWrapper>
      }>
      {({ closePopup }) => (
        <>
          {parentField && <AddFormFieldMenu parentFieldId={parentField.id} onFieldSelect={closePopup} />}

          {options.map((field) => (
            <MenuItemOrigin key={field.id} onClick={addField(field as IField, closePopup)}>
              <Wrapper>
                {getIconForProperty(field.fieldType)}
                <FieldLabel>{field.label}</FieldLabel>
              </Wrapper>
            </MenuItemOrigin>
          ))}
        </>
      )}
    </PopoverModal>
  )
}

export default AddFieldButton
